/**
 * Renewal Shared MUI Theme
 *
 * 이 파일은 shared 스타일 상수들을 활용한 MUI 테마를 정의합니다.
 */

import { createTheme, ThemeOptions } from "@mui/material/styles";
import { COLOR_CONSTANTS } from "../color";
import { TYPOGRAPHY } from "../typography";
import {
  SPACING_UNIT,
  BORDER_RADIUS,
  SHADOWS,
  Z_INDEX,
  BREAKPOINTS,
} from "../spacing";

// Light Theme Palette
const lightPalette: ThemeOptions["palette"] = {
  mode: "light",
  primary: {
    main: COLOR_CONSTANTS.primary.main,
    light: COLOR_CONSTANTS.primary.light,
    dark: COLOR_CONSTANTS.primary.dark,
    contrastText: COLOR_CONSTANTS.primary.contrastText,
  },
  secondary: {
    main: COLOR_CONSTANTS.secondary.main,
    light: COLOR_CONSTANTS.secondary.light,
    dark: COLOR_CONSTANTS.secondary.dark,
    contrastText: COLOR_CONSTANTS.secondary.contrast,
  },
  error: {
    main: COLOR_CONSTANTS.error.main,
    light: COLOR_CONSTANTS.error.light,
    dark: COLOR_CONSTANTS.error.dark,
    contrastText: COLOR_CONSTANTS.error.contrast,
  },
  warning: {
    main: COLOR_CONSTANTS.warning.main,
    light: COLOR_CONSTANTS.warning.light,
    dark: COLOR_CONSTANTS.warning.dark,
    contrastText: COLOR_CONSTANTS.warning.contrast,
  },
  info: {
    main: COLOR_CONSTANTS.info.main,
    light: COLOR_CONSTANTS.info.light,
    dark: COLOR_CONSTANTS.info.dark,
    contrastText: COLOR_CONSTANTS.info.contrast,
  },
  success: {
    main: COLOR_CONSTANTS.success.main,
    light: COLOR_CONSTANTS.success.light,
    dark: COLOR_CONSTANTS.success.dark,
    contrastText: COLOR_CONSTANTS.success.contrast,
  },
  grey: COLOR_CONSTANTS.COLOR.GREY,
  text: {
    primary: COLOR_CONSTANTS.text.primary,
    secondary: COLOR_CONSTANTS.text.secondary,
    disabled: COLOR_CONSTANTS.text.disabled,
  },
  background: {
    default: COLOR_CONSTANTS.common.white_states.main,
    paper: COLOR_CONSTANTS.common.white_states.main,
  },
  divider: COLOR_CONSTANTS.divider.divider,
};

// Dark Theme Palette
const darkPalette: ThemeOptions["palette"] = {
  mode: "dark",
  primary: {
    main: COLOR_CONSTANTS.primary.main,
    light: COLOR_CONSTANTS.primary.light,
    dark: COLOR_CONSTANTS.primary.dark,
    contrastText: COLOR_CONSTANTS.primary.contrastText,
  },
  secondary: {
    main: COLOR_CONSTANTS.secondary.main,
    light: COLOR_CONSTANTS.secondary.light,
    dark: COLOR_CONSTANTS.secondary.dark,
    contrastText: COLOR_CONSTANTS.secondary.contrast,
  },
  error: {
    main: COLOR_CONSTANTS.error.main,
    light: COLOR_CONSTANTS.error.light,
    dark: COLOR_CONSTANTS.error.dark,
    contrastText: COLOR_CONSTANTS.error.contrast,
  },
  warning: {
    main: COLOR_CONSTANTS.warning.main,
    light: COLOR_CONSTANTS.warning.light,
    dark: COLOR_CONSTANTS.warning.dark,
    contrastText: COLOR_CONSTANTS.warning.contrast,
  },
  info: {
    main: COLOR_CONSTANTS.info.main,
    light: COLOR_CONSTANTS.info.light,
    dark: COLOR_CONSTANTS.info.dark,
    contrastText: COLOR_CONSTANTS.info.contrast,
  },
  success: {
    main: COLOR_CONSTANTS.success.main,
    light: COLOR_CONSTANTS.success.light,
    dark: COLOR_CONSTANTS.success.dark,
    contrastText: COLOR_CONSTANTS.success.contrast,
  },
  grey: COLOR_CONSTANTS.COLOR.GREY,
  text: {
    primary: COLOR_CONSTANTS.common.white_states.main,
    secondary: COLOR_CONSTANTS.common.white_states.outlinedBorder,
    disabled: COLOR_CONSTANTS.common.white_states.focusVisible,
  },
  background: {
    default: COLOR_CONSTANTS.COLOR.GREY[900],
    paper: COLOR_CONSTANTS.COLOR.GREY[800],
  },
  divider: COLOR_CONSTANTS.COLOR.GREY[700],
};

// Typography Configuration
const typography: ThemeOptions["typography"] = {
  fontFamily: TYPOGRAPHY.fontFamilies.primary,
  fontWeightLight: TYPOGRAPHY.fontWeights.light,
  fontWeightRegular: TYPOGRAPHY.fontWeights.regular,
  fontWeightMedium: TYPOGRAPHY.fontWeights.medium,
  fontWeightBold: TYPOGRAPHY.fontWeights.bold,
  h1: TYPOGRAPHY.scale.h1,
  h2: TYPOGRAPHY.scale.h2,
  h3: TYPOGRAPHY.scale.h3,
  h4: TYPOGRAPHY.scale.h4,
  h5: TYPOGRAPHY.scale.h5,
  h6: TYPOGRAPHY.scale.h6,
  subtitle1: TYPOGRAPHY.scale.subtitle1,
  subtitle2: TYPOGRAPHY.scale.subtitle2,
  body1: TYPOGRAPHY.scale.body1,
  body2: TYPOGRAPHY.scale.body2,
  button: TYPOGRAPHY.scale.buttonMedium,
  caption1: TYPOGRAPHY.scale.caption1,
  caption2: TYPOGRAPHY.scale.caption2,
  overline: TYPOGRAPHY.scale.overline,
};

// Spacing Configuration
const spacing = (factor: number) => `${SPACING_UNIT * factor}px`;

// Breakpoints Configuration
const breakpoints: ThemeOptions["breakpoints"] = {
  values: BREAKPOINTS,
};

// Shape Configuration
const shape: ThemeOptions["shape"] = {
  borderRadius: parseInt(BORDER_RADIUS.md),
};

// Z-Index Configuration
const zIndex: ThemeOptions["zIndex"] = {
  mobileStepper: Z_INDEX.dropdown,
  fab: Z_INDEX.overlay,
  speedDial: Z_INDEX.overlay,
  appBar: Z_INDEX.sticky,
  drawer: Z_INDEX.modal,
  modal: Z_INDEX.modal,
  snackbar: Z_INDEX.toast,
  tooltip: Z_INDEX.tooltip,
};

// Component Style Overrides
const components: ThemeOptions["components"] = {
  MuiButton: {
    styleOverrides: {
      root: {
        textTransform: "none",
        borderRadius: BORDER_RADIUS.md,
        fontWeight: TYPOGRAPHY.fontWeights.medium,
        boxShadow: "none",
        "&:hover": {
          boxShadow: SHADOWS.sm,
        },
      },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        "& .MuiOutlinedInput-root": {
          borderRadius: BORDER_RADIUS.md,
        },
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: BORDER_RADIUS.lg,
        boxShadow: SHADOWS.md,
      },
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: BORDER_RADIUS.lg,
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: BORDER_RADIUS.full,
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        borderRadius: BORDER_RADIUS.md,
      },
    },
  },
  MuiAppBar: {
    styleOverrides: {
      root: {
        boxShadow: SHADOWS.sm,
      },
    },
  },
  MuiDrawer: {
    styleOverrides: {
      paper: {
        borderRadius: 0,
      },
    },
  },
};

// Light Theme
const lightTheme = createTheme({
  palette: lightPalette,
  typography,
  spacing,
  breakpoints,
  shape,
  zIndex,
  components,
});

// Dark Theme (기존 시스템과 호환)
const darkTheme = createTheme({
  palette: darkPalette,
  typography,
  spacing,
  breakpoints,
  shape,
  zIndex,
  components,
});

// Default Theme (Dark Theme 사용)
const defaultTheme = lightTheme;

// Theme Export
export { lightTheme, darkTheme, defaultTheme };
export default defaultTheme;
